import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import {
  EMPTY_AGGREGATOR_VALUES,
  type AdministratorRow,
  type AggregatorFormValues,
} from "./schema";
import type {
  AdministratorData,
  AggregatorCreationRequestData,
  AggregatorCreationRequestFieldErrors,
  AggregatorCreationRequestPayload,
  ServerError,
} from "./types";

const emptyToUndefined = (value?: string) => (value ? value : undefined);

const toOptionalString = (value?: string | number) =>
  value === undefined || value === null || value === "" ? undefined : String(value);

// A function to adapt values entered in form to a format accepted by the server
export const adaptValues = (
  formValues: AggregatorFormValues,
  administrators: AdministratorRow[]
): AggregatorCreationRequestPayload => ({
  aggregator: {
    name: emptyToUndefined(formValues.name),
    other_info: emptyToUndefined(formValues.other_info),
    contact_email: emptyToUndefined(formValues.contact_email),
    phone: emptyToUndefined(formValues.phone),
    legal_address: emptyToUndefined(formValues.legal_address),
    physical_address: emptyToUndefined(formValues.physical_address),
    district: emptyToUndefined(formValues.district),
    legal_status: emptyToUndefined(formValues.legal_status),
    year_of_establishment: emptyToUndefined(formValues.year_of_establishment),
    org_type: emptyToUndefined(formValues.org_type),
    tier: emptyToUndefined(formValues.tier),
    wings_vendor_id: emptyToUndefined(formValues.wings_vendor_id),
    membership_fee: emptyToUndefined(formValues.membership_fee),
    language: emptyToUndefined(formValues.language),
    membership_fee_needed: formValues.membership_fee_needed,
    administrators: administrators.map(administrator => ({
      person: {
        uuid: administrator.uuid || uuidv4(),
        first_name: emptyToUndefined(administrator.first_name),
        middle_name: emptyToUndefined(administrator.middle_name),
        last_name: emptyToUndefined(administrator.last_name),
        contact_phone: emptyToUndefined(administrator.contact_phone),
        location: emptyToUndefined(administrator.location),
        identification_card_number: emptyToUndefined(administrator.identification_card_number),
        gender: emptyToUndefined(administrator.gender),
        day_of_birth: administrator.day_of_birth
          ? format(administrator.day_of_birth, "yyyy-MM-dd")
          : undefined,
      },
      email: emptyToUndefined(administrator.email),
      position: emptyToUndefined(administrator.position),
    })),
  },
});

// A function to adapt fields errors received from server
export const adaptErrors = (
  error: ServerError
): AggregatorCreationRequestFieldErrors | undefined => {
  if (!error?.data) {
    return undefined;
  }

  const { administrators, ...aggregator } = error.data.aggregator ?? {};

  return {
    aggregator: aggregator as AggregatorCreationRequestFieldErrors["aggregator"],
    administrators: administrators?.map(({ person, ...rest }) => ({
      ...rest,
      ...person,
    })) as AggregatorCreationRequestFieldErrors["administrators"],
  };
};

// A function to adapt values received from server to a format compatible with the form
export const adaptInitialFromValues = (
  data?: AggregatorCreationRequestData
): AggregatorFormValues => {
  const aggregator = data?.aggregator;

  if (!aggregator) {
    return EMPTY_AGGREGATOR_VALUES;
  }

  return {
    name: aggregator.name ?? "",
    other_info: aggregator.other_info ?? "",
    contact_email: aggregator.contact_email ?? "",
    phone: aggregator.phone ?? "",
    legal_address: aggregator.legal_address ?? "",
    physical_address: aggregator.physical_address ?? "",
    district: aggregator.district ?? "",
    legal_status: toOptionalString(aggregator.legal_status) ?? "",
    year_of_establishment: aggregator.year_of_establishment ?? "",
    tier: aggregator.tier ?? "",
    wings_vendor_id: aggregator.wings_vendor_id ?? "",
    org_type: toOptionalString(aggregator.org_type) ?? "",
    membership_fee: aggregator.membership_fee ?? "",
    language: aggregator.language ?? "",
    membership_fee_needed: aggregator.membership_fee_needed ?? false,
  };
};

// A function to adapt administrators received from server to a format compatible with the table
export const adaptInitialAdministrators = (
  data?: AggregatorCreationRequestData
): AdministratorRow[] =>
  (data?.aggregator?.administrators ?? []).map((administrator: AdministratorData) => {
    const person = administrator.person ?? {};

    return {
      id: person.uuid ?? uuidv4(),
      uuid: person.uuid ?? uuidv4(),
      first_name: person.first_name ?? "",
      middle_name: person.middle_name ?? "",
      last_name: person.last_name ?? "",
      contact_phone: person.contact_phone ?? "",
      location: person.location ?? "",
      identification_card_number: person.identification_card_number ?? "",
      gender: person.gender ?? "",
      day_of_birth: person.day_of_birth ? new Date(person.day_of_birth) : null,
      email: administrator.email ?? "",
      position: administrator.position ?? "",
    };
  });
